import type { Tone } from "./tone";
import { cn } from "@/lib/utils";

/* The fill, which is the only part a caller chooses. The track is one opaque
   grey: a translucent one lets a tinted panel through, and the unfilled part
   stops reading as the remainder. */
const FILL: Record<Tone, string> = {
  success: "bg-success",
  warning: "bg-warning",
  info: "bg-info",
  destructive: "bg-destructive",
  provisional: "bg-provisional",
  neutral: "bg-muted-foreground",
};

/**
 * How far something has got.
 *
 * Three spellings before this, at three heights and three colours, and no two
 * agreed on what a reader is told. Two were bare `div`s with no role at all,
 * and the third a native `<progress>` with no accessible name, so a screen
 * reader announced a percentage without saying of what.
 *
 * A `div` with the role rather than the native element: `<progress>` is styled
 * differently by every engine, and the two that were divs were divs because of
 * it. The role and the three values are what a reader actually needs.
 */
export function ProgressBar({
  value,
  max,
  label,
  tone = "info",
  className,
}: {
  value: number;
  max: number;
  /** What is progressing, for a reader who cannot see the bar. */
  label: string;
  tone?: Tone;
  className?: string;
}) {
  // A zero maximum is nothing to do rather than everything done, and dividing
  // by it reads as NaN, which lays out as an empty track on one engine and a
  // full one on another. There is no range to be anywhere in, so the bar says
  // so by naming no value at all: that is what an indeterminate one is.
  const counted = max > 0;
  const within = Math.min(Math.max(value, 0), max);
  return (
    <div
      role="progressbar"
      aria-label={label}
      // The announced value is clamped with the drawn one. A bar full to its
      // end that says twelve of eight is read aloud as a hundred and fifty
      // per cent, which is a thing no reader can act on.
      aria-valuenow={counted ? within : undefined}
      aria-valuemin={counted ? 0 : undefined}
      aria-valuemax={counted ? max : undefined}
      className={cn(
        "h-1.5 w-full overflow-hidden rounded-full bg-muted",
        className,
      )}
    >
      <div
        className={cn("h-full rounded-full transition-[width]", FILL[tone])}
        style={{ width: `${String(counted ? (within / max) * 100 : 0)}%` }}
      />
    </div>
  );
}
